import type { NodeLayout } from '../../../utils/treeLayout';
import type { StrategyNode } from '../../../types/BaseNode';

interface Props {
    layoutMap: Map<string, NodeLayout>;
    nodes: StrategyNode[];
    filterParentIds: Set<string>;
    comparisonParentIds: Set<string>;
    nodeMetadata?: Record<string, unknown>;
    offset: number;
}

function extractCount(metadata: unknown): number | null {
    if (typeof metadata === 'number' && Number.isFinite(metadata)) {
        return metadata;
    }

    if (typeof metadata === 'string') {
        const parsed = Number(metadata);
        return Number.isFinite(parsed) ? parsed : null;
    }

    if (!metadata || typeof metadata !== 'object') {
        return null;
    }

    const record = metadata as Record<string, unknown>;
    const candidates = [record.count, record.row_count, record.output_row_count, record.result_count];
    for (const candidate of candidates) {
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
            return candidate;
        }
        if (typeof candidate === 'string') {
            const parsed = Number(candidate);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }
    }

    return null;
}

export function TreeConnectors({ layoutMap, nodes, filterParentIds, comparisonParentIds, nodeMetadata, offset }: Props) {
    const edges: React.ReactElement[] = [];

    // Pre-calculate node config map for fast lookup
    const nodeConfigs = new Map<string, { 
        type: string; 
        operator?: string; 
        exitMode?: string;
        holdDuration?: number;
        holdType?: string;
    }>();
    const stack = [...nodes];
    while (stack.length > 0) {
        const n = stack.pop()!;
        nodeConfigs.set(n.id, { 
            type: n.type, 
            operator: (n.config as any)?.operator,
            exitMode: (n as any).config?.exitMode,
            holdDuration: (n as any).config?.holdDuration,
            holdType: (n as any).config?.holdType
        });
        if (n.type === 'Composite') {
            if (n.config.left) stack.push(n.config.left);
            if (n.config.right) stack.push(n.config.right);
        } else if (n.type === 'Formatter' && n.config.child) {
            stack.push(n.config.child);
        }
    }

    // --- 1. Draw all edges (Lines between child top and parent bottom) ---
    for (const layout of layoutMap.values()) {
        if (!layout.parentId) continue; // root has no parent

        const parent = layoutMap.get(layout.parentId);
        if (!parent) continue;

        // ── Anchor points ───────────────────────────────────────────────────
        // Child: exit from the very top-center of the card (Data flows UP)
        const childCx = layout.x + offset + layout.width / 2;
        const childTopY = layout.y + offset;  

        // Parent: enter at the bottom center of the card
        const parentCx = parent.x + offset + parent.width / 2;
        const parentPortY = parent.y + offset + parent.height; 

        // Junction point below parent bottom
        const STEM_LEN = 30; 
        const junctionY = parentPortY + STEM_LEN;

        // ── Cubic Bezier Path ───────────────────────────────────────────────
        const verticalPull = Math.max(40, Math.abs(childTopY - junctionY) * 0.4);
        const cp1y = childTopY - verticalPull;    
        const cp2y = junctionY + verticalPull;   

        const d = [
            `M ${childCx} ${childTopY}`,
            `C ${childCx} ${cp1y}, ${parentCx} ${cp2y}, ${parentCx} ${junctionY}`,
            `L ${parentCx} ${parentPortY}`,
        ].join(' ');

        const parentConfig = layout.parentId ? nodeConfigs.get(layout.parentId) : null;
        const isFilterParent = layout.parentId ? filterParentIds.has(layout.parentId) : false;
        const isComparisonParent = layout.parentId ? comparisonParentIds.has(layout.parentId) : false;
        const isVarianceTargetSlot = parentConfig?.type === 'Variance' && layout.slot === 'entry';
        const isHoldExit = parentConfig?.type === 'PnL' && layout.slot === 'exit' && (parentConfig as any).exitMode === 'hold';

        // Pick color by slot/node type
        const strokeColor = isVarianceTargetSlot
            ? 'var(--connector-variance)'
            : (layout.slot === 'left' || layout.slot === 'entry') ? 'var(--connector-left)'
                : (layout.slot === 'right' || layout.slot === 'exit') ? 'var(--connector-right)'
                    : 'var(--connector-formatter)';

        const isRightLegOfConditional = (isFilterParent || isComparisonParent) && layout.slot === 'right';

        edges.push(
            <g key={`${layout.parentId}->${layout.id}`}>
                {/* Wide glow pass */}
                <path d={d} fill="none" stroke={strokeColor} strokeWidth={10} strokeLinecap="round" opacity={0.12} />

                {/* Main line */}
                <path
                    d={d}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={2}
                    strokeLinecap="round"
                    opacity={(isRightLegOfConditional || isHoldExit) ? 0.7 : 0.9} 
                    strokeDasharray={isHoldExit ? "4,4" : (isRightLegOfConditional ? "10,10" : "none")} 
                />

                {/* Animated dashes */}
                {!isRightLegOfConditional && !isHoldExit && (
                    <path
                        d={d}
                        fill="none"
                        stroke="var(--text-primary)"
                        strokeWidth={1.5}
                        strokeDasharray="6 18"
                        strokeLinecap="round"
                        opacity={0.3}
                        style={{ animation: 'dash-up 2s linear infinite' }}
                    />
                )}

                {/* Hold Shift Label */}
                {isHoldExit && (
                    <g>
                        <path
                            id={`path-hold-${layout.id}`}
                            d={childCx > parentCx ? [
                                `M ${parentCx} ${parentPortY}`,
                                `L ${parentCx} ${junctionY}`,
                                `C ${parentCx} ${cp2y}, ${childCx} ${cp1y}, ${childCx} ${childTopY}`,
                            ].join(' ') : [
                                `M ${childCx} ${childTopY}`,
                                `C ${childCx} ${cp1y}, ${parentCx} ${cp2y}, ${parentCx} ${junctionY}`,
                                `L ${parentCx} ${parentPortY}`,
                            ].join(' ')}
                            fill="none"
                            stroke="transparent"
                            strokeWidth={20}
                        />
                        <text
                            dy="-6"
                            style={{
                                fill: 'var(--text-muted)',
                                fontSize: '9px',
                                fontWeight: '500',
                                letterSpacing: '0.02em',
                                pointerEvents: 'none'
                            }}
                        >
                            <textPath
                                href={`#path-hold-${layout.id}`}
                                startOffset="50%"
                                textAnchor="middle"
                            >
                                time shifted {parentConfig.holdDuration} {parentConfig.holdType}{Number(parentConfig.holdDuration) > 1 ? 's' : ''}
                            </textPath>
                        </text>
                    </g>
                )}
            </g>
        );
    }

    // --- 2. Render Exactly One Output Badge Per Node (Always ABOVE the node) ---
    const badges: React.ReactElement[] = [];
    if (nodeMetadata) {
        for (const [nodeId, layout] of layoutMap.entries()) {
            if (nodeMetadata[nodeId]) {
                const metadata = nodeMetadata[nodeId];
                const count = extractCount(metadata);
                
                if (count !== undefined && count !== null) {
                    const childCx = layout.x + offset + layout.width / 2;
                    const childTopY = layout.y + offset;

                    let bx = childCx;
                    let by = childTopY - 12; // Default centered above root

                    if (layout.parentId) {
                        const parent = layoutMap.get(layout.parentId);
                        if (parent) {
                            const parentCx = parent.x + offset + parent.width / 2;
                            const parentPortY = parent.y + offset + parent.height;
                            const STEM_LEN = 30;
                            const junctionY = parentPortY + STEM_LEN;

                            const verticalPull = Math.max(40, Math.abs(childTopY - junctionY) * 0.4);
                            const cp1y = childTopY - verticalPull;    
                            const cp2y = junctionY + verticalPull;   

                            // Exact Cubic Bezier point at t=0.33
                            // B(t) = (1-t)^3*P0 + 3(1-t)^2*t*P1 + 3(1-t)*t^2*P2 + t^3*P3
                            const t = 0.33;
                            const mt = 1 - t;
                            
                            bx = (mt ** 3) * childCx + 
                                 3 * (mt ** 2) * t * childCx + 
                                 3 * mt * (t ** 2) * parentCx + 
                                 (t ** 3) * parentCx;
                                 
                            by = (mt ** 3) * childTopY + 
                                 3 * (mt ** 2) * t * cp1y + 
                                 3 * mt * (t ** 2) * cp2y + 
                                 (t ** 3) * junctionY;
                        }
                    }

                    badges.push(
                        <JunctionBadge
                            key={`badge-out-${nodeId}`}
                            x={bx} y={by} count={count}
                            isOutput={!layout.parentId} // Use output style for roots
                            label={!layout.parentId ? "Final Result" : undefined}
                        />
                    );
                }
            }
        }
    }

    return (
        <>
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    overflow: 'visible',
                    zIndex: 0,
                }}
            >
                <defs>
                    <style>{`
          @keyframes dash-up {
            from { stroke-dashoffset: 48; }
            to   { stroke-dashoffset: 0; }
          }
        `}</style>
                </defs>
                {edges}
            </svg>
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    overflow: 'visible',
                    zIndex: 5,
                }}
            >
                {badges}
            </svg>
        </>
    );
}

// ── Shared Junction Badge Component ──────────────────────────────────────────

interface BadgeProps {
    x: number;
    y: number;
    count: number;
    isOutput?: boolean;
    label?: string;
}

function JunctionBadge({ x, y, count, isOutput, label }: BadgeProps) {
    const isError = count === 0;
    const countDisplay = count > 9999 ? `${(count / 1000).toFixed(1)}k` : count.toLocaleString();
    
    // Pick background colors based on state - More visible than before
    const bgColor = isError 
        ? 'rgba(127, 29, 29, 0.95)' // Deep red
        : isOutput 
            ? 'rgba(30, 41, 59, 0.95)' // Dark slate for root output
            : 'rgba(15, 23, 42, 0.98)'; // Darker for general outputs
            
    const borderColor = isError
        ? 'rgba(239, 68, 68, 0.8)'
        : isOutput 
            ? 'rgba(71, 85, 105, 0.6)'
            : 'rgba(51, 65, 85, 0.5)';

    return (
        <g transform={`translate(${x}, ${y})`}>
            {/* Shield shape / Pill */}
            <rect
                x="-16" y="-8" width="32" height="16" rx="6"
                style={{
                    fill: bgColor,
                    stroke: borderColor,
                    strokeWidth: isError ? 1.5 : 1,
                    backdropFilter: 'blur(4px)',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
                    pointerEvents: 'auto'
                }}
            >
                <title>{`${label ? label + ' : ' : ''}${count.toLocaleString()} rows`}</title>
            </rect>
            
            {/* Context label if needed */}
            {label && (
                <text
                    y="-12" textAnchor="middle" 
                    style={{ fontSize: '7px', fill: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase'}}
                >
                    {label}
                </text>
            )}

            <text
                dy="0.5" textAnchor="middle" dominantBaseline="middle"
                style={{
                    fill: isError ? '#fca5a5' : isOutput ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontSize: '9px',
                    fontWeight: 700,
                    pointerEvents: 'none',
                    letterSpacing: '-0.01em'
                }}
            >
                {countDisplay}
            </text>
        </g>
    );
}
